/**
 * Export-Plus-Seite (Sprint 5, Feb 2026).
 *
 * Liefert SB-konforme Daten-Exporte für Steuerberater-Software:
 *   * BMD-XML (AT)               — österreichischer SB-Marktführer
 *   * DATEV-AT-CSV (AT)          — DATEV mit AT-EKR-Konten
 *   * DATEV Belegtransfer (DE)   — DBM-V2 ZIP mit XML + Original-PDFs
 *
 * UI-Logik:
 *   1. Periode wählen (Quartal/Monat/Jahr)
 *   2. Format wählen (gefiltert nach country)
 *   3. Download → File-Stream + Toast
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/utils/api";
import { downloadBlob, extractBlobError } from "@/utils/downloadBlob";
import {
  FileBox, Download, FileText, Globe, Database, Package,
} from "lucide-react";


function PeriodPicker({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    // 7 Jahre rückwärts für historische Exporte (z.B. Buchhaltungs-Aufbewahrungsfristen § 147 AO / § 132 BAO)
    return Array.from({ length: 7 }, (_, i) => currentYear - i);
  }, [currentYear]);
  const monthLabels = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="exports-period-picker">
      <div>
        <Label className="text-xs text-slate-600 mb-1.5 block">Zeitraum-Typ</Label>
        <Select
          value={value.period_type}
          onValueChange={(v) => onChange({ ...value, period_type: v, period_index: v === "yearly" ? null : 1 })}
        >
          <SelectTrigger data-testid="exports-period-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monatlich</SelectItem>
            <SelectItem value="quarterly">Quartalsweise</SelectItem>
            <SelectItem value="yearly">Jährlich</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-slate-600 mb-1.5 block">Jahr</Label>
        <Select value={String(value.year)} onValueChange={(v) => onChange({ ...value, year: Number(v) })}>
          <SelectTrigger data-testid="exports-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {value.period_type !== "yearly" && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block">
            {value.period_type === "monthly" ? "Monat" : "Quartal"}
          </Label>
          <Select value={String(value.period_index || 1)} onValueChange={(v) => onChange({ ...value, period_index: Number(v) })}>
            <SelectTrigger data-testid="exports-period-index"><SelectValue /></SelectTrigger>
            <SelectContent>
              {value.period_type === "monthly"
                ? monthLabels.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)
                : [1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)
              }
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}


const FORMAT_ICON = {
  bmd: Database,
  datev_at: FileText,
  dbm_v2: Package,
};


export default function ExportsPage() {
  const [period, setPeriod] = useState({
    period_type: "quarterly",
    year: new Date().getFullYear(),
    period_index: Math.floor(new Date().getMonth() / 3) + 1,
  });
  const [formats, setFormats] = useState([]);
  const [country, setCountry] = useState("DE");
  const [loadingFormat, setLoadingFormat] = useState(null);
  // Berater-/Mandantennummer (Pflicht für BMD/DATEV-AT-Import)
  const [clientNumbers, setClientNumbers] = useState({ consultant_number: "", client_number: "" });

  const load = useCallback(async () => {
    try {
      const res = await api.get("/exports/formats");
      setFormats(res.data.formats || []);
      setCountry(res.data.country || "DE");
    } catch {
      toast.error("Formate konnten nicht geladen werden");
    }
    try {
      const cn = await api.get("/exports/client-numbers");
      setClientNumbers({
        consultant_number: cn.data.consultant_number || "",
        client_number: cn.data.client_number || "",
      });
    } catch { /* optional */ }
  }, []);

  const saveClientNumbers = useCallback(async (next) => {
    try {
      const res = await api.put("/exports/client-numbers", next);
      setClientNumbers({
        consultant_number: res.data.consultant_number || "",
        client_number: res.data.client_number || "",
      });
    } catch {
      toast.error("Nummern konnten nicht gespeichert werden");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (fmt) => {
    setLoadingFormat(fmt.id);
    try {
      const res = await api.post(`/exports/${fmt.id}`, period, { responseType: "blob" });
      const mime = fmt.extension === "zip" ? "application/zip" :
                    fmt.extension === "xml" ? "application/xml" : "text/csv";
      downloadBlob(res, `ivoliq_${fmt.id}_${period.year}.${fmt.extension}`, mime);
      toast.success(`${fmt.name} heruntergeladen`);
    } catch (e) {
      toast.error(await extractBlobError(e, "Export fehlgeschlagen"));
    } finally {
      setLoadingFormat(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="exports-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <FileBox className="h-7 w-7 text-purple-600" />
          Export Plus
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Datei-Übergabe an Steuerberater-Software (BMD, DATEV) — fertig formatiert für den Direktimport.
        </p>
      </div>

      {/* Country-Hint */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-3 flex items-start gap-3">
          <Globe className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <span className="font-medium">Land:</span>{" "}
            {country === "AT" ? "🇦🇹 Österreich" : country === "DE" ? "🇩🇪 Deutschland" : country}.
            Verfügbare Formate werden automatisch nach deinem Land gefiltert. Land änderbar in den Stammdaten.
          </div>
        </CardContent>
      </Card>

      {/* Period Picker */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Periode wählen</CardTitle>
          <CardDescription>
            Steuerschema (Ist/Soll) + Konten-Mapping aus deinen Stammdaten werden automatisch angewendet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PeriodPicker value={period} onChange={setPeriod} />
        </CardContent>
      </Card>

      {/* Berater-/Mandantennummer — Pflicht für BMD-Import (AT) */}
      {country === "AT" && (
        <Card data-testid="exports-client-numbers-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Kanzlei-Zuordnung (BMD / DATEV-AT)</CardTitle>
            <CardDescription>
              Berater- und Mandantennummer sind <strong>Pflichtfelder</strong> für den Import beim
              Steuerberater (z. B. BMD NTCS). Frag die Nummern bei deiner Kanzlei ab.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(!clientNumbers.consultant_number || !clientNumbers.client_number) && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="exports-client-numbers-warning"
              >
                ⚠️ Ohne Berater- und Mandantennummer schlägt der Import in der Kanzlei i. d. R. fehl
                (FIRMENNUMMER / BERATERNUMMER bleiben leer).
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Beraternummer</Label>
                <Input
                  value={clientNumbers.consultant_number}
                  onChange={(e) => setClientNumbers((p) => ({ ...p, consultant_number: e.target.value }))}
                  onBlur={(e) => saveClientNumbers({ ...clientNumbers, consultant_number: e.target.value.trim() })}
                  placeholder="z. B. 12345"
                  data-testid="exports-consultant-number"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Mandanten-/Firmennummer</Label>
                <Input
                  value={clientNumbers.client_number}
                  onChange={(e) => setClientNumbers((p) => ({ ...p, client_number: e.target.value }))}
                  onBlur={(e) => saveClientNumbers({ ...clientNumbers, client_number: e.target.value.trim() })}
                  placeholder="z. B. 70001"
                  data-testid="exports-client-number"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format-Cards */}
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-slate-700 uppercase tracking-wide">
          Verfügbare Formate ({formats.length})
        </h2>
        {formats.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-slate-500" data-testid="exports-empty">
              Keine Formate für dein Land verfügbar.{" "}
              <a href="/settings" className="underline text-blue-600 hover:text-blue-700">
                Land in den Stammdaten ändern
              </a>{" "}
              um relevante SB-Übergabe-Formate zu sehen.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {formats.map((fmt) => {
              const Icon = FORMAT_ICON[fmt.id] || FileText;
              const isLoading = loadingFormat === fmt.id;
              return (
                <Card
                  key={fmt.id}
                  className="hover:border-purple-300 transition-colors"
                  data-testid={`exports-card-${fmt.id}`}
                >
                  <CardContent className="p-5 flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="h-5 w-5 text-purple-600" />
                        <h3 className="text-base font-semibold text-slate-900">{fmt.name}</h3>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
                          .{fmt.extension}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2">{fmt.description}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(fmt)}
                      disabled={isLoading}
                      className="shrink-0"
                      data-testid={`exports-download-${fmt.id}`}
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {isLoading ? "Erstelle…" : "Download"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
